import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ProductSelect } from '../../components/ProductSelect';
import { listStockProductsPage } from '../../data/fetchers';
import { unitMatchesBarcode } from '../../lib/stockUnits';
import type { Product } from '../../types/domain';

export function useStockCatalog(ids: string[] = []) {
  const known = useRef(new Map<string, Product>());
  const [, refresh] = useState(0);
  const [lookupError, setLookupError] = useState('');
  const remember = (product: Product) => {
    known.current.set(product.id, product);
    refresh((n) => n + 1);
  };
  const hydration = useQuery({
    queryKey: ['stock-products', 'ids', [...new Set(ids)].sort()],
    queryFn: async () => {
      const unique = [...new Set(ids)];
      const products: Product[] = [];
      for (let i = 0; i < unique.length; i += 50)
        products.push(
          ...(
            await listStockProductsPage({
              ids: unique.slice(i, i + 50),
              limit: 50,
              status: 'all',
            })
          ).items,
        );
      return products;
    },
    enabled: ids.length > 0,
  });
  useEffect(() => {
    if (hydration.data) {
      hydration.data.forEach((p) => known.current.set(p.id, p));
      refresh((n) => n + 1);
    }
  }, [hydration.data]);
  const lookup = async (barcode: string) => {
    setLookupError('');
    try {
      const page = await listStockProductsPage({
        search: barcode,
        limit: 20,
        status: 'active',
      });
      const product = page.items.find((p) =>
        p.units.some((u) => unitMatchesBarcode(u, barcode)),
      );
      if (product) remember(product);
      return product;
    } catch {
      setLookupError('ค้นหาบาร์โค้ดไม่สำเร็จ กรุณาลองใหม่');
      return undefined;
    }
  };
  return {
    clear: () => {
      known.current.clear();
      refresh((n) => n + 1);
    },
    lookupError,
    loading: ids.length > 0 && hydration.isPending,
    error: hydration.isError,
    products: [...known.current.values()],
    productOf: (id: string) => known.current.get(id),
    remember,
    lookup,
  };
}

/** A failed response retries the same intent; edits create a new intent. */
export function useStockWriteIntent() {
  const current = useRef<{
    key: string;
    requestId: string;
    occurredAt: string;
  } | null>(null);
  const pending = useRef(false);
  return {
    begin: (payload: unknown) => {
      if (pending.current) return null;
      const key = JSON.stringify(payload);
      if (current.current?.key !== key)
        current.current = {
          key,
          requestId: crypto.randomUUID(),
          occurredAt: new Date().toISOString(),
        };
      pending.current = true;
      return {
        requestId: current.current.requestId,
        occurredAt: current.current.occurredAt,
      };
    },
    finish: (success: boolean) => {
      pending.current = false;
      if (success) current.current = null;
    },
  };
}

export function StockProductSelect({
  catalog,
  ...props
}: { catalog: ReturnType<typeof useStockCatalog> } & Omit<
  Parameters<typeof ProductSelect>[0],
  'products'
>) {
  const [search, setSearch] = useState<string | null>(null);
  const consumedSearch = useRef<string | null>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
      setOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  const result = useQuery({
    queryKey: ['stock-products', 'search', query, offset],
    queryFn: () =>
      listStockProductsPage({
        search: query ?? '',
        limit: 20,
        offset,
        status: 'active',
      }),
    enabled: query !== null,
  });
  const results = result.data?.items ?? [];
  const selected = catalog.productOf(props.value);
  useEffect(() => {
    if (
      !query ||
      query !== search ||
      query === consumedSearch.current ||
      result.isFetching
    )
      return;
    const product = results.find((p) =>
      p.units.some((u) => unitMatchesBarcode(u, query)),
    );
    if (product && product.id !== props.value) {
      consumedSearch.current = query;
      catalog.remember(product);
      props.onChange(product.id, query.trim());
    }
  }, [result.data, query, props.value, result.isFetching, search]);

  return (
    <div>
      <ProductSelect
        {...props}
        products={[
          ...(result.isFetching ? [] : results),
          ...(selected && !results.some((p) => p.id === selected.id)
            ? [selected]
            : []),
        ]}
        onSearchChange={(next) => {
          consumedSearch.current = null;
          setSearch(next);
        }}
        onBarcodeLookup={catalog.lookup}
        onChange={(id, barcode) => {
          consumedSearch.current = query;
          const product =
            results.find((p) => p.id === id) ?? catalog.productOf(id);
          if (product) catalog.remember(product);
          props.onChange(id, barcode);
        }}
      />
      {catalog.lookupError && <p role="alert">{catalog.lookupError}</p>}
      {result.isFetching && <span role="status">กำลังค้นหา…</span>}
      {result.isError && (
        <p role="alert">
          ค้นหาสินค้าไม่สำเร็จ{' '}
          <button type="button" onClick={() => void result.refetch()}>
            ลองใหม่
          </button>
        </p>
      )}
      {(result.data?.total ?? 0) > 20 && (
        <div className="flex gap-3 mt-1">
          <button
            type="button"
            disabled={offset === 0 || result.isFetching}
            onClick={() => setOffset((n) => n - 20)}
          >
            สินค้าก่อนหน้า
          </button>
          <span>
            {offset + 1}–{offset + results.length} / {result.data?.total}
          </span>
          <button
            type="button"
            disabled={
              offset + 20 >= (result.data?.total ?? 0) || result.isFetching
            }
            onClick={() => setOffset((n) => n + 20)}
          >
            สินค้าถัดไป
          </button>
        </div>
      )}
    </div>
  );
}
